// useUploadsDescribe - name a set of uploads by id, for rendering citations.
//
// Deliberately by id rather than by project: a citation in a cross-project
// conversation names an upload on whichever project the agent read it from, so
// the project-scoped useUploadsList cannot resolve it. That is why those
// citations rendered as raw text.
//
// Metadata only (name, kind, whether a text layer exists, where it sits in Drive), so this stays small
// however many citations a page carries.
import { useQuery } from "react-query";

import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

export interface UploadDescription {
  // For a global file this is its global_data_id: agents cite both the same way.
  upload_id: string;
  // Which viewer opens it: a project upload, or a global file.
  source: "upload" | "global";
  // A global file's kind: "slack_day" opens as the day's messages.
  kind: string;
  project_id: string | null;
  original_name: string;
  // What a reader is shown: an email thread's subject, otherwise the inventory's name.
  display_name: string;
  content_type: string;
  extracted_text_chars: number | null;
  // Where the original sits in Drive; null for a file that did not come from Drive.
  drive_file_id: string | null;
  drive_path: string | null;
}

interface DescribeResponse {
  uploads: UploadDescription[];
}


export interface DescribedUploads {
  byId: Map<string, UploadDescription>;
  /** The query has answered. Distinct from an empty map, which is also what a
   *  still-loading query looks like - conflating the two leaves a citation to a
   *  missing document showing "Finding the cited source…" for ever, with no way
   *  out of it. */
  resolved: boolean;
}

/** Names for `uploadIds`. Ids the platform has no row for are simply absent: a
 *  citation naming a document that does not exist is a finding the caller
 *  renders, not an error here. */
export const useUploadsDescribe = (uploadIds: string[]): DescribedUploads => {
  const http = useAgentsHttp();
  // Sorted + deduped so the same page does not mint a new cache key per render,
  // and a document cited ten times is fetched once.
  const ids = Array.from(new Set(uploadIds)).sort();
  const { data, isSuccess, isError } = useQuery<DescribeResponse, Error>(
    [QK.uploadsDescribe, ids.join(",")],
    () => http<DescribeResponse>("/uploads/describe", { upload_ids: ids }),
    { enabled: ids.length > 0, staleTime: 5 * 60 * 1000 },
  );
  return {
    byId: new Map((data?.uploads ?? []).map((u) => [u.upload_id, u])),
    // A failed lookup counts as resolved: "we could not find it" is an answer a
    // reader can act on, where a permanent spinner is not.
    resolved: ids.length === 0 || isSuccess || isError,
  };
};
